import type {
  PlanogramBayNonCircular,
  PlanogramBayPallet,
  PlanogramPalletAccessSide,
} from "@CommonTypes/merchflow/pog/bay";
import type { PlanogramShelfNonCircular } from "@CommonTypes/merchflow/pog/shelf";

/** A pallet holds at most this many SKUs unless its fixture says fewer. */
export const PALLET_DEFAULT_MAX_SKUS = 4;

export const PALLET_UNDER_BAY_SHARE = 0.5;

/** Under a gondola bay the shelves' uprights close a pallet's sides. */
export const PALLET_UNDER_BAY_ACCESS_SIDES: PlanogramPalletAccessSide[] = ["front"];

/**
 * The sides a shopper reaches a pallet from, by where it stands in its row: a
 * pallet with a neighbour on both sides is reached from the front only, and
 * the pallet at an end of the row from the front, the back and its open side.
 * A pallet standing alone is an end on both sides.
 */
export const getPalletAccessSides = ({
  hasLeftNeighbour,
  hasRightNeighbour,
}: {
  hasLeftNeighbour: boolean;
  hasRightNeighbour: boolean;
}): PlanogramPalletAccessSide[] => {
  if (hasLeftNeighbour && hasRightNeighbour) {
    return ["front"];
  }
  return [
    "front",
    "back",
    ...(hasLeftNeighbour ? [] : (["left"] as const)),
    ...(hasRightNeighbour ? [] : (["right"] as const)),
  ];
};

const PALLET_NOTCH_SPACING = 100;

export const getPalletLaneUniqueId = (bayUniqueId: string, laneNo: number) =>
  `${bayUniqueId}-lane-${laneNo}`;

/**
 * A pallet as a bay: the deck on the floor, and one shelf per depth lane, the
 * front lane first. The lanes all stand on the deck; their notch numbers only
 * keep them in order, since shelves are kept top notch first.
 */
export const createPalletBay = ({
  uniqueId,
  width,
  depth,
  deckBottom = 0,
  deckHeight,
  pallet,
}: {
  uniqueId: string;
  width: number;
  depth: number;
  /** Where the deck stands from the floor; a pallet on the floor is 0. */
  deckBottom?: number;
  deckHeight: number;
  pallet: PlanogramBayPallet;
}): PlanogramBayNonCircular => {
  const deckTop = deckBottom + deckHeight;
  const shelves: PlanogramShelfNonCircular[] = Array.from({ length: pallet.lanes }, (_, index) => {
    const laneNo = index + 1;
    return {
      bay: uniqueId,
      uniqueId: getPalletLaneUniqueId(uniqueId, laneNo),
      width,
      merchandisableWidth: width,
      deadSpans: [],
      thickness: deckHeight,
      depth: pallet.laneDepth,
      type: "REGULAR",
      offset: 0,
      notchNo: pallet.lanes - laneNo + 1,
      mergedLeft: null,
      mergedRight: null,
      grillHeight: 0,
      items: [],
      variantRestrictions: [],
      weightCapacity: null,
      align: "left",
      laneNo,
    };
  });

  return {
    uniqueId,
    width,
    height: deckTop + pallet.maxHeight,
    depth,
    notchOffset: deckTop,
    baseHeight: deckHeight,
    notchSpacing: PALLET_NOTCH_SPACING,
    maxNotch: pallet.lanes,
    maxProductHead: 0,
    maxShelfHeight: 0,
    pallet,
    shelves,
    pegboards: [],
  };
};
